import { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import ListaDesplegable from "../../ComponentesGlobales/ListaDesplegable";
import { MensajeToastExito } from "../../componentes/FuncionesLocales";
import { agregarOperadorCaja, listarOperadoresDisponiblesCaja } from "../../servicios/cajasApi";
import {
  crearControladorBusquedaOperadoresCaja,
  crearRequestAgregarOperadorCaja,
  extraerOperadoresDisponiblesCaja,
  respuestaCaja,
} from "../../servicios/cajas";

const mensajeDato = (respuesta) => String(respuesta?.data?.Dato ?? respuesta?.data?.dato ?? "").trim();

export default function CajaOperadorSelector({
  EmpresaId,
  cajaSesionId,
  operadorAccionId,
  pcId,
  habilitado,
  onAgregado,
}) {
  const [seleccionado, setSeleccionado] = useState(null);
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [error, setError] = useState("");
  const [agregando, setAgregando] = useState(false);
  const [ultimaBusqueda, setUltimaBusqueda] = useState("");

  const controlador = useMemo(() => crearControladorBusquedaOperadoresCaja({
    consultar: async (request, signal) => {
      const respuesta = await listarOperadoresDisponiblesCaja(request, { signal });
      if (respuesta?.aborted) throw new DOMException("Solicitud cancelada", "AbortError");
      const mensaje = respuestaCaja(respuesta).error;
      if (mensaje) throw new Error(mensaje);
      return extraerOperadoresDisponiblesCaja(respuesta);
    },
    alLimpiar: () => {
      setResultados([]);
      setBuscando(false);
      setError("");
    },
    alBuscar: () => {
      setBuscando(true);
      setError("");
    },
    alResolver: (lista) => {
      setResultados(lista.map((operador) => ({
        ...operador,
        sDescripcion: operador.etiqueta,
      })));
      setBuscando(false);
    },
    alError: (fallo) => {
      setResultados([]);
      setBuscando(false);
      setError(fallo?.message || "No se pudieron buscar los operadores.");
    },
  }), []);

  useEffect(() => () => controlador.destruir(), [controlador]);

  useEffect(() => {
    setSeleccionado(null);
    setResultados([]);
    setError("");
    setUltimaBusqueda("");
    controlador.actualizar({ EmpresaId, cajaSesionId: habilitado ? cajaSesionId : 0, busqueda: "" });
  }, [cajaSesionId, controlador, habilitado, EmpresaId]);

  const buscar = (query) => {
    const busqueda = String(query ?? "").trim();
    setUltimaBusqueda(busqueda);
    controlador.actualizar({ EmpresaId, cajaSesionId, busqueda });
  };

  const agregar = async () => {
    if (agregando || !habilitado || Number(seleccionado?.id) <= 0) return;
    setAgregando(true);
    try {
      const respuesta = await agregarOperadorCaja(crearRequestAgregarOperadorCaja({
        EmpresaId,
        cajaSesionId,
        operadorId: seleccionado.id,
        operadorAccionId,
        pcId,
      }));
      const mensaje = respuestaCaja(respuesta).error;
      if (mensaje) {
        Swal.fire("Error", mensaje, "error");
        return;
      }
      MensajeToastExito(mensajeDato(respuesta) || "Operador agregado correctamente.");
      setSeleccionado(null);
      setResultados([]);
      setUltimaBusqueda("");
      await onAgregado?.();
      controlador.actualizar({ EmpresaId, cajaSesionId, busqueda: "" }, { forzar: true });
    } catch (fallo) {
      Swal.fire("Error", fallo?.message || "No se pudo agregar el operador.", "error");
    } finally {
      setAgregando(false);
    }
  };

  const reintentar = () => controlador.actualizar(
    { EmpresaId, cajaSesionId, busqueda: ultimaBusqueda },
    { forzar: true },
  );

  const itemTemplate = (operador) => (
    <div className="cajas-operador-opcion">
      <strong>{operador.etiqueta}</strong>
      {operador.mail && operador.mail !== operador.etiqueta && <small>{operador.mail}</small>}
    </div>
  );

  return (
    <div className="cajas-operadores-acciones">
      <div className="cajas-operador-buscador">
        <ListaDesplegable
          Lista={resultados}
          Buscando={buscando}
          Error={error}
          Reintentar={reintentar}
          BuscarRemoto={buscar}
          setValor={setSeleccionado}
          Seleccionado={seleccionado}
          ItemTemplate={itemTemplate}
          MensajeVacio="No hay operadores disponibles"
          MostrarEstado={false}
          ActualizarSoloAlSeleccionar
          Deshabilitado={!habilitado || agregando}
          Placeholder="Buscar operador"
        />
      </div>
      <button type="button" className="primario" onClick={agregar} disabled={agregando || Number(seleccionado?.id) <= 0}>
        <i className={`pi ${agregando ? "pi-spin pi-spinner" : "pi-user-plus"}`} /> {agregando ? "Agregando..." : "Agregar"}
      </button>
    </div>
  );
}
